import { useChat } from "@ai-sdk/react";
import { HUB_BASE_URL } from "@constants/env";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import {
	captureErrorToSentry,
	serializeErrorForSentry,
} from "@helpers/sentry-logger";
import { HUB_ENDPOINTS } from "@memorang/constants/urls";
import type { BaseItemSchema } from "@memorang/types";
import { DefaultChatTransport } from "ai";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SuperJSON from "superjson";

import { PTE_FEEDBACK_AGENT_ID } from "../constants/pte/feedback";
import { agentStreamFetch } from "../helpers/agent-stream";
import { extractItemContent } from "../helpers/extract-item-content";
import { computeClientGrading } from "../helpers/feedback-client-grading";
import { postFormativeFeedbackAnswerEvent } from "../helpers/post-ai-answer-event";
import type {
	PteFeedbackPhase,
	PteFeedbackUIMessage,
} from "../types/pte-feedback";
import useAIFeedbackStore from "./useAIFeedbackStore";
import useCurrentSession from "./useCurrentSession";
import useSessionStore from "./useSessionStore";

const transport = new DefaultChatTransport<PteFeedbackUIMessage>({
	api: `${HUB_BASE_URL}${HUB_ENDPOINTS.FEEDBACK_AGENT}`,
	fetch: agentStreamFetch,
});

const serializeUserResponse = (value: unknown): string => {
	if (typeof value === "string") {
		return value;
	}
	if (Array.isArray(value)) {
		return value.length ? JSON.stringify(value) : "";
	}
	if (value && typeof value === "object") {
		return Object.keys(value).length ? JSON.stringify(value) : "";
	}
	return "";
};

// Map HIWL `word-<index>` ids to actual words for agent feedback.
const resolveHighlightedWords = (
	item: BaseItemSchema,
	selectedChoiceIds: string[],
): string[] => {
	const words = (item.content?.stem?.text ?? "").split(/\s+/).filter(Boolean);
	return selectedChoiceIds
		.map((id) => words[Number(id.replace("word-", ""))])
		.filter((word): word is string => Boolean(word));
};

// Feedback items store their answer in different slots: choice/inline selections
// (HCSL/HIWL) live in itemStates.selectedChoiceIds; the rest (FIB*/WFDL/ROPR)
// funnel into useAIFeedbackStore.userResponse.
const resolveUserResponse = (item: BaseItemSchema): unknown => {
	const itemId = item.id;
	if (itemId == null) {
		return undefined;
	}
	const responseType = item.response?.type;
	if (
		responseType === "choice" ||
		responseType === "OPTION_SELECTION" ||
		responseType === "INLINE_TEXT_SELECTION"
	) {
		const selectedChoiceIds =
			useSessionStore.getState().itemStates?.get(itemId)?.selectedChoiceIds ??
			[];
		if (responseType === "INLINE_TEXT_SELECTION") {
			return resolveHighlightedWords(item, selectedChoiceIds);
		}
		return selectedChoiceIds;
	}
	return useAIFeedbackStore.getState().userResponse?.[itemId];
};

const resolveFinishPhase = (
	isAbort: boolean,
	isStreamCompleted: boolean,
): PteFeedbackPhase => {
	if (isAbort) {
		return "idle";
	}
	return isStreamCompleted ? "ready" : "error";
};

const reduceFeedbackText = (parts: PteFeedbackUIMessage["parts"]): string => {
	let feedbackText = "";
	for (const part of parts) {
		if (
			part.type === "data-feedback-stream" &&
			typeof part.data.feedback === "string"
		) {
			feedbackText = part.data.feedback;
		}
	}
	return feedbackText;
};

export const usePteFeedbackAgent = () => {
	const { userId } = useAuthContext();
	const {
		currentExamContext: { projectId, organizationId },
	} = useExamContext();
	const { currentItem } = useCurrentSession();
	const sessionId = useSessionStore((s) => s.id);
	const { i18n } = useTranslation();

	const setStreaming = useAIFeedbackStore((s) => s.setStreaming);
	const updateExplanation = useAIFeedbackStore((s) => s.updateExplanation);

	const currentItemRef = useRef(currentItem);
	currentItemRef.current = currentItem;
	const targetItemIdRef = useRef<number | undefined>(undefined);
	const lastFeedbackRef = useRef("");
	const [phase, setPhase] = useState<PteFeedbackPhase>("idle");

	const { messages, sendMessage, setMessages, stop } =
		useChat<PteFeedbackUIMessage>({
			transport,
			onFinish: ({ message, isAbort, isDisconnect, isError }) => {
				const isStreamCompleted = !(isAbort || isDisconnect || isError);
				setPhase(resolveFinishPhase(isAbort, isStreamCompleted));
				setStreaming(false);

				const targetId = targetItemIdRef.current;
				const feedbackText =
					message?.role === "assistant"
						? reduceFeedbackText(message.parts ?? [])
						: "";
				if (!(targetId && feedbackText && isStreamCompleted)) {
					return;
				}

				updateExplanation(targetId, feedbackText);
				useAIFeedbackStore.getState().updateStatus(targetId, "COMPLETE");
				postFormativeFeedbackAnswerEvent(targetId, feedbackText);
			},
			onError: (err) => {
				setPhase("error");
				setStreaming(false);
				captureErrorToSentry(new Error("PTE feedback stream failed"), {
					error: serializeErrorForSentry(err),
				});
			},
		});

	useEffect(() => {
		const targetId = targetItemIdRef.current;
		if (!targetId || messages.length === 0) {
			return;
		}
		const last = messages[messages.length - 1];
		if (last?.role !== "assistant") {
			return;
		}
		const feedbackText = reduceFeedbackText(last.parts ?? []);
		if (feedbackText && feedbackText !== lastFeedbackRef.current) {
			lastFeedbackRef.current = feedbackText;
			updateExplanation(targetId, feedbackText);
		}
	}, [messages, updateExplanation]);

	const currentItemId = currentItem?.id;
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset feedback state on item change
	useEffect(() => {
		stop();
		setPhase("idle");
		setStreaming(false);
		setMessages([]);
		targetItemIdRef.current = undefined;
		lastFeedbackRef.current = "";
	}, [currentItemId, setMessages, setStreaming, stop]);

	const generate = useCallback(() => {
		if (useAIFeedbackStore.getState().streaming) {
			return;
		}

		const item = currentItemRef.current;
		const itemId = item?.id;
		if (!(itemId && userId)) {
			return;
		}

		targetItemIdRef.current = itemId;
		lastFeedbackRef.current = "";
		setMessages([]);
		updateExplanation(itemId, "");
		setStreaming(true);
		setPhase("submitting");

		const resolvedResponse = resolveUserResponse(item);
		const userResponse = serializeUserResponse(resolvedResponse);
		const clientGrading = computeClientGrading(item, resolvedResponse);

		sendMessage(
			{ text: "Generate" },
			{
				body: {
					agentId: PTE_FEEDBACK_AGENT_ID,
					inputs: SuperJSON.serialize({
						item: extractItemContent(item),
						userResponse,
						outputLanguage: i18n.language,
						...(clientGrading ? { clientGrading } : {}),
					}),
					userId,
					projectId,
					organizationId,
					itemId,
					...(sessionId ? { sessionId } : {}),
				},
			},
		);
		setPhase("streaming");
	}, [
		userId,
		projectId,
		organizationId,
		sessionId,
		i18n.language,
		sendMessage,
		setMessages,
		setStreaming,
		updateExplanation,
	]);

	return { generate, phase };
};
